/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'es-toolkit/compat';
import { useSelector } from 'react-redux';

import { DOJ_OPTIONS } from 'Deer/components/CategoryMultiSelect/DOJComp';

import { REVIEW_PRESET_TYPES } from 'screens/admin/Reviews/ReviewProcessV2Intent/constants/reviewCreationConstants';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

// import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

// export const REVIEWERS_PEER_POOLS = {
//   SENIORS: 'SENIORS',
//   JUNIORS: 'JUNIORS',
//   OUTSIDE_TEAM: 'OUTSIDE_TEAM',
//   PRIMARY_TEAM: 'PRIMARY_TEAM',
// };

export const REVIEW_FORM_STAGE = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  PUBLISHED: 'PUBLISHED',
};

export const AUTO_UPDATE_STATUSES = {
  IS_SAVING: 'IS_SAVING',
  UPDATED: 'UPDATED',
  IS_ERROR: 'IS_ERROR',
};

export const REVIEW_PROVIDER_TYPE_LABELS_V1 = {
  [REVIEW_PROVIDER_TYPES.SELF]: 'Self',
  [REVIEW_PROVIDER_TYPES.MANAGER]: 'Final reviewer',
  [REVIEW_PROVIDER_TYPES.PEER]: 'Peer',
  [REVIEW_PROVIDER_TYPES.UPWARD]: 'Upward',
  [REVIEW_PROVIDER_TYPES.DLM]: 'Additional reviewer',
  [REVIEW_PROVIDER_TYPES.EXTERNAL]: 'External reviewer',
};

export const REVIEW_PROVIDER_TYPE_DESCRIPTIONS_V1 = {
  [REVIEW_PROVIDER_TYPES.SELF]: 'The employee provides their own self-assessment.',
  [REVIEW_PROVIDER_TYPES.MANAGER]:
    'The primary manager (or equivalent) who consolidates all feedback and gives the final assessment before calibration.',
  [REVIEW_PROVIDER_TYPES.PEER]:
    'A colleague or stakeholder who shares feedback based on working with the employee; they cannot see others’ feedback.',
  [REVIEW_PROVIDER_TYPES.UPWARD]: 'A direct report who reviews their manager.',
  [REVIEW_PROVIDER_TYPES.DLM]:
    'A manager-level reviewer who provides input after seeing others’ feedback, but whose assessment is not final.',
};

const initialReviewData = {
  reviewCycleId: null,
  // used in case of multi review (30-60-90)
  feedbackFormId: null,
  enabledProviderTypes: [],
  providerTypeToTemplateMap: {},

  reviewerSettings: {
    isAutoAcceptPeerNomination: false,
    isAutoAcceptDlmNomination: false,
    isAllowPeerNominationBySelf: false,
    minPeers: 0,
    maxPeers: 0,
    selfAnswerVisibilityForPeers: [],
  },

  // -------- for one time review ----------------
  reviewPeriodStart: null,
  reviewPeriodEnd: null,
  // reviewPeriodStart: '2025-03-27',
  // reviewPeriodEnd: '2025-04-16',
  timelines: {},
  reminders: {},

  // ------ for continuous(triggered) review --------
  reviewPeriodDetails: {},

  providerTimelineDetails: {},

  calibrationAndDeliverables: {
    isAutoPublish: false,
    calibrationQuestion: null,
    outcomeQuestion: null,
  },
  autoPublishOffsetDays: 0,

  gridDefinition: null,
  // ui only calibration grid metadata
  gridMetaData: {
    boxCount: 0,
    rowsCount: 0,
    columnsCount: 0,
  },
};

const initialState = {
  reviewCycleId: null,
  selectedReviewCycleId: null,

  // just for presets page on UI
  selectedReviewCreationPreset: REVIEW_PRESET_TYPES.CUSTOM,

  // reviewCycleId: null,
  reviewStage: REVIEW_FORM_STAGE.DRAFT,
  // // --- IMPORTANT: NEED ARRAY OF THIS F
  // feedbackFormId: null,
  reviewToFeedbackGroupIdMapping: {},

  // Fields
  reviewCycleName: '',
  isDuplicateName: false,
  isDuplicateNameError: '',
  reviewType: null,
  isAllEmployees: true,
  // all the employees of org
  totalEmployeeCount: 0,
  // the applicable employees for review
  selectedEmployeeCount: 0,

  // just for PIP (based on Review Outcome) review
  triggerSourceReviewCycleDetails: {
    reviewCycleId: null,
    outcomeScore: null,
  },

  // triggerDaysCount: 30,
  triggerType: null,

  isConfirmationModalOpen: false,

  autoUpdateStatus: AUTO_UPDATE_STATUSES.UPDATED,

  // configure panels
  isFormsSettingsPanelOpen: false,
  isTimelineRemindersPanelOpen: false,
  isValidationPanelOpen: false,
  isCalibrationAndDeliverablesPanelOpen: false,
  calibrationAndDeliverablesPanelScrollSection: null,

  // final checkboxes
  isSendLaunchComm: false,
  isLaunchConfirmationChecked: false,

  selected30_60_90_index: 0,
  selectedReviewId_30_60_90: null,

  // ---------------This is required cause in 30_60_90 mode we need to maintain separate data for 3 reviews
  // in case of non 30_60_90 mode, this index will be 0 and we will use reviewData[0] only
  reviewData: [cloneDeep(initialReviewData), cloneDeep(initialReviewData), cloneDeep(initialReviewData)],
};

const getTargetReviewDataIndex = (state) => {
  if (!state.selectedReviewCycleId) return 0;
  const index = state.reviewData.findIndex((data) => data.reviewCycleId === state.selectedReviewCycleId);
  return index === -1 ? 0 : index;
};

const getTargetReviewDataIndexFromId = (state, id) => {
  if (!id) return null;
  const index = state.reviewData.findIndex((data) => data.reviewCycleId === id);
  return index === -1 ? 0 : index;
};

export const getDOJFilterInitialValue = (selectedFilters) => {
  // handle to get the DOJ filter direction and set "type" key which is used in UI only
  // if only startDate is present, it means after date is selected
  // if only endDate is present, it means before date is selected
  // if both are present, it means between
  const { DATE_OF_JOINING, ...restSelectedFilters } = selectedFilters ?? {};
  // if date filter is present
  const { startDate, endDate } = DATE_OF_JOINING?.[0] ?? {};

  const DOJFilter = {
    type:
      startDate && endDate
        ? DOJ_OPTIONS[1].value
        : startDate
          ? DOJ_OPTIONS[2].value
          : endDate
            ? DOJ_OPTIONS[0].value
            : null,
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  };
  return {
    // DOJ filter is an array because BE is sending it in such way
    DATE_OF_JOINING: [DOJFilter],
    ...restSelectedFilters,
  };
};

export const reviewCreationV1 = createSlice({
  name: 'reviewCreationV1',
  initialState,
  reducers: {
    resetReviewCreation: () => initialState,
    // setReviewCreationStep: (state, action) => {
    //   state.step = action.payload;
    // },

    setCommonReviewCreationKey: (state, action) => {
      Object.keys(action.payload).forEach((key) => {
        // special case for 30_60_90 multi review setup
        if (key === 'reviewToFeedbackGroupIdMapping') {
          const reviewIds = Object.keys(action.payload[key] ?? {}).map((id) => Number(id));
          reviewIds.forEach((reviewId, index) => {
            state.reviewData[index].reviewCycleId = reviewId;
          });
        }

        if (key === 'selectedFilters') {
          const selectedFiltersWithDOJ = getDOJFilterInitialValue(action.payload[key]);
          state[key] = selectedFiltersWithDOJ;
          return;
        }
        state[key] = action.payload[key];
      });
    },

    setReviewCreationKey: (state, action) => {
      const selectedReviewIndex =
        getTargetReviewDataIndexFromId(state, action?.payload?.targetReviewCycleId) ?? getTargetReviewDataIndex(state);
      Object.keys(action.payload).forEach((key) => {
        if (key === 'reviewPeriodDetails' && action.payload[key] === null) {
          state.reviewData[selectedReviewIndex].reviewPeriodDetails = {};
          return;
        }
        if (key === 'providerTimelineDetails' && action.payload[key] === null) {
          state.reviewData[selectedReviewIndex].providerTimelineDetails = {};
          return;
        }
        state.reviewData[selectedReviewIndex][key] = action.payload[key];
      });
      // state = { ...state, ...action.payload };
      // return state;
    },

    setReviewCreationReviewerSetting: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);

      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].reviewerSettings[key] = action.payload[key];
      });
      // state = { ...state, ...action.payload };
      // return state;
    },

    setReviewPeriodDetails: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);

      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].reviewPeriodDetails[key] = action.payload[key];
      });
      // state = { ...state, ...action.payload };
      // return state;
    },

    setReviewCreationTimelines: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);

      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].timelines[key] = action.payload[key];
      });
      // state = { ...state, ...action.payload };
      // return state;
    },

    setReviewDateOffset: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);
      Object.keys(action.payload).forEach((key) => {
        // biome-ignore lint/suspicious/useIterableCallbackReturn: No explanation provided in original eslint-disable
        action.payload[key].map(({ offsetType, value }) => {
          state.reviewData[selectedReviewIndex].providerTimelineDetails[key][offsetType] = value;
        });
      });
    },

    setReviewCreationCalibrationAndDeliverables: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);
      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables[key] = action.payload[key];
      });
    },

    setReviewDeliverableSelfViewQuestionSetting: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);
      const { formType, questionId, isAllQuestionsToggle, isViewAllowed } = action.payload;

      const questionTypeToAnswerVisibility =
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables?.answerVisibilityForSelf?.MANAGER
          ?.questionTypeToAnswerVisibility ?? {};

      const formData = questionTypeToAnswerVisibility?.[formType] ?? {
        sectionVisibilityType: null,
        selectedQuestionIds: [],
      };

      // all questions case
      if (isAllQuestionsToggle) {
        if (isViewAllowed) {
          formData.sectionVisibilityType = 'ALL_QUESTIONS';
          formData.selectedQuestionIds = null;
        } else {
          formData.sectionVisibilityType = 'SELECTED_QUESTIONS';
          formData.selectedQuestionIds = [];
        }

        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.answerVisibilityForSelf = {
          MANAGER: {
            questionTypeToAnswerVisibility: {
              ...questionTypeToAnswerVisibility,
              [formType]: formData,
            },
          },
        };
        return;
      }

      const allFormQuestions =
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.templateQuestionData?.MANAGER?.questionDataMap[
          formType
        ] ?? {};
      const allFormQuestionsIds = Object.keys(allFormQuestions).map((key) => Number(key));
      const questionsCount = allFormQuestionsIds.length;
      // question wise case

      let newSelectedQuestionIds = formData.selectedQuestionIds || [];
      if (isViewAllowed) {
        // enabling
        if (!newSelectedQuestionIds.includes(questionId)) {
          newSelectedQuestionIds.push(questionId);
        }
      } else if (formData.sectionVisibilityType === 'ALL_QUESTIONS') {
        // disabling
        // special case: when all are selected and selectedQuestionIds is null
        // get all question ids
        newSelectedQuestionIds = allFormQuestionsIds.filter((id) => id !== questionId);
      } else {
        const index = newSelectedQuestionIds.indexOf(questionId);
        if (index !== -1) {
          newSelectedQuestionIds.splice(index, 1);
        }
      }

      formData.selectedQuestionIds = newSelectedQuestionIds;

      // override if question selection/deselection results in all questions selection/deselection

      if (newSelectedQuestionIds.length === questionsCount) {
        formData.sectionVisibilityType = 'ALL_QUESTIONS';
        formData.selectedQuestionIds = null;
      } else if (newSelectedQuestionIds.length === 0) {
        formData.sectionVisibilityType = 'SELECTED_QUESTIONS';
        formData.selectedQuestionIds = [];
      } else {
        formData.sectionVisibilityType = 'SELECTED_QUESTIONS';
      }

      state.reviewData[selectedReviewIndex].calibrationAndDeliverables.answerVisibilityForSelf = {
        MANAGER: {
          questionTypeToAnswerVisibility: {
            ...questionTypeToAnswerVisibility,
            [formType]: formData,
          },
        },
      };
    },

    setReviewCreationCalibrationQuestion: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);

      if (!state.reviewData[selectedReviewIndex].calibrationAndDeliverables.calibrationQuestion) {
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.calibrationQuestion = {};
      }
      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.calibrationQuestion[key] = action.payload[key];
      });
    },

    setReviewCreationOutcomeQuestion: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);
      if (!state.reviewData[selectedReviewIndex].calibrationAndDeliverables.outcomeQuestion) {
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.outcomeQuestion = {};
      }
      Object.keys(action.payload).forEach((key) => {
        state.reviewData[selectedReviewIndex].calibrationAndDeliverables.outcomeQuestion[key] = action.payload[key];
      });
    },

    setReviewCreationGridDefinition: (state, action) => {
      const selectedReviewIndex = getTargetReviewDataIndex(state);

      if (action.payload) {
        const rowsCount = Object.keys(action.payload).length;
        const columnsCount = Object.keys(action.payload['1']).length;
        const isGridDimensionsChanged =
          state.reviewData[selectedReviewIndex].gridMetaData.rowsCount !== 0 &&
          state.reviewData[selectedReviewIndex].gridMetaData.columnsCount !== 0 && // if rows and columns count is 0, it means the grid is not set yet, this will not be counted as a change
          (state.reviewData[selectedReviewIndex].gridMetaData.rowsCount !== rowsCount ||
            state.reviewData[selectedReviewIndex].gridMetaData.columnsCount !== columnsCount);
        state.reviewData[selectedReviewIndex].gridMetaData.rowsCount = rowsCount;
        state.reviewData[selectedReviewIndex].gridMetaData.columnsCount = columnsCount;
        state.reviewData[selectedReviewIndex].gridMetaData.boxCount = rowsCount * columnsCount;
        // don't overwrite in case of dimension change and there is already a grid definition for this dimension
        if (
          isGridDimensionsChanged &&
          !!state.reviewData[selectedReviewIndex].gridDefinition[`${rowsCount}x${columnsCount}`]
        ) {
          return;
        }
        if (state.reviewData[selectedReviewIndex].gridDefinition) {
          state.reviewData[selectedReviewIndex].gridDefinition[`${rowsCount}x${columnsCount}`] = action.payload;
        } else {
          state.reviewData[selectedReviewIndex].gridDefinition = {};
          state.reviewData[selectedReviewIndex].gridDefinition[`${rowsCount}x${columnsCount}`] = action.payload;
        }
      } else {
        state.reviewData[selectedReviewIndex].gridMetaData.rowsCount = 0;
        state.reviewData[selectedReviewIndex].gridMetaData.columnsCount = 0;
        state.reviewData[selectedReviewIndex].gridMetaData.boxCount = 0;
        state.reviewData[selectedReviewIndex].gridDefinition = null;
      }
    },
  },
});

export const {
  resetReviewCreation,
  setReviewCreationKey,
  setCommonReviewCreationKey,
  setReviewCreationReviewerSetting,
  setReviewPeriodDetails,
  setReviewCreationTimelines,
  setReviewDateOffset,

  setReviewCreationCalibrationAndDeliverables,
  setReviewCreationCalibrationQuestion,
  setReviewCreationGridDefinition,
  setReviewCreationOutcomeQuestion,
  setReviewDeliverableSelfViewQuestionSetting,
} = reviewCreationV1.actions;

export default reviewCreationV1.reducer;

// selectors
export const useCommonReviewCreationV1Data = () => useSelector((state) => state.reviewCreationV1);

export const useReviewCreationCurrentReviewCycleId = () => {
  const selectedReviewCycleId = useSelector((state) => state.reviewCreationV1.selectedReviewCycleId);
  return { selectedReviewCycleId };
};

export const useReviewFeedbackFormId = () =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);
    if (selectedReviewIndex === 0) return state.reviewCreationV1.feedbackFormId;
    // else 30_60_90 case
    const feedbackFormIdsMap = state.reviewCreationV1.reviewToFeedbackGroupIdMapping || {};
    const feedbackFormIds = Object.values(feedbackFormIdsMap);
    return feedbackFormIds[selectedReviewIndex] || null;
  });

export const useAllReviewCyclesData = () => useSelector((state) => state.reviewCreationV1.reviewData);

export const useReviewCreationV1Data = () =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);

    return state.reviewCreationV1.reviewData[selectedReviewIndex];
  });

export const useReviewCreationDerivedMetaData = () => {
  const { enabledProviderTypes } = useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);
    return state.reviewCreationV1.reviewData[selectedReviewIndex];
  });

  return {
    hasPeerForm: enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.PEER),
    hasDLMForm: enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.DLM),
    hasSelfForm: enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.SELF),
    hasUpwardForm: enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.UPWARD),
  };
};

export const useSelectReviewCreationCalibrationAndDeliverables = () =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);

    return state.reviewCreationV1.reviewData[selectedReviewIndex].calibrationAndDeliverables;
  });

export const useSelectReviewCreationGridMetaData = () =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);
    return state.reviewCreationV1.reviewData[selectedReviewIndex].gridMetaData;
  });

export const useGridDefinition = () => {
  const { rowsCount, columnsCount } = useSelectReviewCreationGridMetaData();

  return useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);

    return (
      state.reviewCreationV1.reviewData[selectedReviewIndex].gridDefinition?.[`${rowsCount}x${columnsCount}`] ?? null
    );
  });
};

export const useSelectCalibrationSelfViewAllowed = ({ questionId = null, formType, isAllQuestionsToggle = false }) =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);

    const visibilityData =
      state.reviewCreationV1.reviewData[selectedReviewIndex].calibrationAndDeliverables.answerVisibilityForSelf;
    if (!visibilityData) {
      return false;
    }
    const formTypeData = visibilityData.MANAGER.questionTypeToAnswerVisibility[formType];
    if (!formTypeData) {
      return false;
    }

    // because of dynamic nature of forms, meaning questions can be added or removed too.
    // if all question toggle is selected we send this special enum value in api, instead of all question id arrays.
    if (formTypeData.sectionVisibilityType === 'ALL_QUESTIONS') {
      return true;
    }

    // this early return because in all question toggle, we are only interested if all questions are selected
    if (isAllQuestionsToggle) {
      if (formTypeData.selectedQuestionIds?.length === 0) {
        // no question selected
        return false;
      }
      // neither all questions are selected and nor 0 questions are selected
      return null;
    }

    return (formTypeData.selectedQuestionIds ?? []).includes(questionId);
  });

export const useSelectedSubReviewIndex = () =>
  useSelector((state) => {
    const selectedReviewIndex = getTargetReviewDataIndex(state.reviewCreationV1);

    return selectedReviewIndex;
  });
